import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6nobcstj1ja1m9goqfg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', businessName: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-gradient-to-b from-black to-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div id="about">
            <div className="inline-block px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
              <span className="text-sm text-gray-300 font-medium">About GWDevelopers · Nashville, TN</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
              Nashville&apos;s AI-Powered Web Development Agency
            </h2>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-6">
              GWDevelopers is a <strong className="text-white">Nashville, TN web development agency</strong> built to help small businesses establish a professional online presence. We use AI-powered tools to build websites faster and more affordably than traditional Nashville web agencies.
            </p>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-8">
              Whether you&apos;re in Downtown Nashville, East Nashville, Franklin, or Brentwood — we build websites that rank locally, load fast, and turn visitors into customers. Many great Nashville businesses miss out on new customers simply because they don&apos;t have a professional website.
            </p>

            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 sm:p-8 mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">Meet the Founder</h3>
              <p className="text-lg sm:text-xl font-semibold text-white mb-3">Josh Gee</p>
              <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
                I&apos;m dedicated to helping Nashville small businesses build a strong online presence at a reasonable cost. My mission is to give every local business a clean, modern website that builds trust, ranks in local search, and helps customers find them online.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6 mb-6">
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-lg sm:text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Call us directly</p>
                  <a href="tel:740-641-7292" className="text-base sm:text-lg font-semibold hover:text-gray-300 transition-colors cursor-pointer">
                    740-641-7292
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-line text-lg sm:text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Serving</p>
                  <p className="text-base sm:text-lg font-semibold">Nashville, TN &amp; Middle Tennessee</p>
                </div>
              </div>
            </div>
          </div>

          <div id="get-started">
            <div className="bg-gray-950 border border-white/10 rounded-3xl p-6 sm:p-10">
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-block px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-4">
                  <span className="text-sm text-gray-300 font-medium">Get Started</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">Request Your Free Website Preview</h3>
                <p className="text-gray-400 text-sm sm:text-base">Fill out the form below and we'll get back to you within 24 hours</p>
              </div>

              <form onSubmit={handleSubmit} data-readdy-form id="free-preview-form">
                <div className="space-y-5 sm:space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-gray-500 focus:border-white focus:outline-none transition-colors text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-gray-500 focus:border-white focus:outline-none transition-colors text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-gray-500 focus:border-white focus:outline-none transition-colors text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="businessName"
                      placeholder="Business Name"
                      value={formData.businessName}
                      onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-gray-500 focus:border-white focus:outline-none transition-colors text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Tell us about your business and what you need"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      maxLength={500}
                      rows={4}
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-gray-500 focus:border-white focus:outline-none transition-colors resize-none text-sm sm:text-base"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="mt-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <p className="text-green-400 text-sm text-center">Thank you! We'll contact you within 24 hours.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-400 text-sm text-center">Something went wrong. Please try again.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-6 sm:mt-8 py-4 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Request Free Preview'}
                  <i className="ri-arrow-right-line"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}